import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';
import { AllNotificationsResponse } from '../interface/get-all-notifications-response';
import { MarkPayload } from '../interface/mark-payload';

import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { PlacementHistoryRequest } from 'src/app/shared/interfaces/placement-record';
import { CaseAssignment } from '../../../shared/interfaces/case-management';
import { ConsentsForReferral } from '../../consent/interfaces/consent';
import { ActionsResponse, StageActions } from '../interface/actions-response';
import { DashboardFilters } from '../interface/dashboard-filters';
import { DashboardRequest } from '../interface/dashboard-request';
import {
  DashboardEvalResponse,
  DashboardIEPResponse,
  DashboardPlacementStatus,
  PlacementRescindRequest,
  PlacementRescindResponse,
  DashboardReferralResponse,
  DashboardResponse,
  HeaderResponse,
} from '../interface/dashboard-response';
import { NotificationSummaryResponse } from '../interface/get-all-notifications-response';
import { KPI } from '../interface/kpi-response';
import { PersonResponse } from '../interface/person';
import { SubmitCaseAssignModel } from '../models/submit-case-assign-model';
import { IepSummaryResponse } from '../interface/iep-summary.response';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { CapatureFirstAttendResponse } from 'src/app/shared/interfaces/capture-first-attend';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  config: IAppConfig;
  private reloadEvalGrid: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  reloadEvalGridData$: Observable<boolean> = this.reloadEvalGrid.asObservable();

  //TODO: Not sure? why null hasbeen using here will fix it later:
  private refreshDashboardGridSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(null);
  refreshDashboardGridSubject$: Observable<boolean | null> =
    this.refreshDashboardGridSubject.asObservable();

  private closeActionOnDashboardSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(false);
  closeActionOnDashboardSubject$: Observable<boolean | null> =
    this.closeActionOnDashboardSubject.asObservable();

  closeActionOnDashboardEvalSubject: BehaviorSubject<boolean | null> =
    new BehaviorSubject<boolean | null>(false);
  reloadDashboardSubject$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  reloadGrid: BehaviorSubject<boolean | null> = new BehaviorSubject<
    boolean | null
  >(false);
  reloadGridSubject$: Observable<boolean | null> =
    this.reloadGrid.asObservable();

  notificationIdSubject: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(null);
  notificationIdSubject$: Observable<number | null> =
    this.notificationIdSubject.asObservable();

  private searchData: string = '';
  private sortByNameData: string = '';
  private sortByDateData: string = '';
  private filterData: DashboardFilters;
  private caseLoadData: boolean = true;
  private caseProcessData: number = 0;
  private routed: boolean = false;
  isNotificationReloadData: boolean;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  saveDashboardData(
    searchData: string,
    sortByNameData: string,
    sortByDateData: string,
    filterData: DashboardFilters,
    caseLoadData: boolean,
    caseProcessData: number
  ): void {
    this.searchData = searchData;
    this.sortByNameData = sortByNameData;
    this.sortByDateData = sortByDateData;
    this.filterData = filterData;
    this.caseLoadData = caseLoadData;
    this.caseProcessData = caseProcessData;
  }

  saveRouted(routed: boolean): void {
    this.routed = routed;
  }

  getSearchData(): string {
    return this.searchData;
  }

  setNotificationReloadData(searchData: string): void {
    this.searchData = searchData;
    this.caseLoadData = false; // As we are unsure if record is present in caseload, setting it to false
    this.isNotificationReloadData = true;
  }

  getNotificationReloadData(): boolean {
    const data: boolean = this.isNotificationReloadData;
    this.isNotificationReloadData = null; // clear after first use
    return data;
  }

  getSortByNameData(): string {
    return this.sortByNameData;
  }

  getSortByDateData(): string {
    return this.sortByDateData;
  }

  getFilterData(): DashboardFilters {
    return this.filterData;
  }

  getCaseLoadData(): boolean {
    return this.caseLoadData;
  }

  getCaseProcessdata(): number {
    return this.caseProcessData;
  }

  getRouted(): boolean {
    return this.routed;
  }

  getCloseActionOnDashboard(form: boolean): void {
    this.closeActionOnDashboardSubject.next(form);
  }

  setNotificationId(form: number | null): void {
    this.notificationIdSubject.next(form);
  }

  getReloadDashboardGrid(form: boolean): void {
    this.refreshDashboardGridSubject.next(form);
  }

  getReloadGridDetails(form: boolean): void {
    this.reloadEvalGrid.next(form);
  }

  public getEvalsData(studentSk?: string): Observable<DashboardEvalResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/Evaluation/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<DashboardEvalResponse>(url)
      .pipe(catchError(handleError));
  }

  public getDashboardData(
    request: DashboardRequest
  ): Observable<DashboardResponse[]> {
    return this.http
      .post<DashboardResponse[]>(
        `${this.config.apiUrl}/Dashboard/Dashboard`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getStudentHeaderDetails(
    studentId: string
  ): Observable<HeaderResponse[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/StudentHeader/${encodeURIComponent(studentId)}`,
      this.config.apiUrl
    );
    return this.http
      .get<HeaderResponse[]>(url)
      .pipe(catchError(handleError));
  }

  public getReferralData(
    studentSk?: string
  ): Observable<DashboardReferralResponse[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/Referral/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<DashboardReferralResponse[]>(url)
      .pipe(catchError(handleError));
  }

  public getIEPData(studentSk?: string): Observable<DashboardIEPResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/IEP/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<DashboardIEPResponse>(url)
      .pipe(catchError(handleError));
  }

  public getPlacementData(
    studentSk?: string
  ): Observable<DashboardPlacementStatus> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/Placement/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<DashboardPlacementStatus>(
        `${this.config.apiUrl}/Dashboard/Placement/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getPlacementsToRescindByNycId(
    nycid: string
  ): Observable<CapatureFirstAttendResponse> {
    return this.http
      .get<CapatureFirstAttendResponse>(
        `${this.config.apiUrl}/PlacementFinder/GetRescindPlacements?studentId=${nycid}`
      )
      .pipe(catchError(handleError));
  }

  public getActionItems(studentSk?: string): Observable<ActionsResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/ActionItems/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ActionsResponse>(url)
      .pipe(catchError(handleError));
  }

  public getKPISummary(): Observable<KPI[]> {
    return this.http
      .get<KPI[]>(`${this.config.apiUrl}/Dashboard/KPISummary`)
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public SubmitCaseAssignment(
    request: SubmitCaseAssignModel
  ): Observable<ApiResponse<CaseAssignment>> {
    return this.http
      .post<ApiResponse<CaseAssignment>>(
        `${this.config.apiUrl}/CaseAssignment/AddCase`,
        request
      )
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public getCaseAssignment(): Observable<PersonResponse[]> {
    return this.http
      .get<PersonResponse[]>(`${this.config.apiUrl}/CaseAssignment/Staff`)
      .pipe(catchError(handleError));
  }

  public getEvalActionItems(
    studentSk?: string,
    evaluationSk?: string
  ): Observable<StageActions> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/EvalActionItems/${encodeURIComponent(evaluationSk)}/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StageActions>(url)
      .pipe(catchError(handleError));
  }

  // TODO: wouldn't this method be in its own service file and not part of the dashboard-service file?
  public getPlacementHistory(
    caseActionSk?: string
  ): Observable<PlacementHistoryRequest[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PlacementFinder/PlacementRecord/${encodeURIComponent(caseActionSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<PlacementHistoryRequest[]>(url)
      .pipe(catchError(handleError));
  }

  public getConsentsForReferral(
    referralSK?: string
  ): Observable<ConsentsForReferral[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/GetConsentsForReferral/${encodeURIComponent(referralSK)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ConsentsForReferral[]>(url)
      .pipe(catchError(handleError));
  }

  public getAllNotifications(
    pageNumber: number,
    pageSize: number,
    nycId: string
  ): Observable<AllNotificationsResponse[]> {
    return this.http
      .get<AllNotificationsResponse[]>(
        `${this.config.apiUrl}/AppNotification?pageNumber=${pageNumber}&pageSize=${pageSize}&studentId=${nycId}`
      )
      .pipe(catchError(handleError));
  }

  public markNotification(
    payload: MarkPayload[]
  ): Observable<ApiResponse<string>> {
    return this.http
      .post<ApiResponse<string>>(
        `${this.config.apiUrl}/AppNotification`,
        payload
      )
      .pipe(catchError(handleError));
  }

  public getNotificationSummary(): Observable<NotificationSummaryResponse> {
    return this.http
      .get<NotificationSummaryResponse>(
        `${this.config.apiUrl}/AppNotification/GetNotificationSummary`
      )
      .pipe(catchError(handleError));
  }

  public markAllRead(): Observable<HttpStatusCode> {
    return this.http
      .get<HttpStatusCode>(`${this.config.apiUrl}/AppNotification/MarkAllRead`)
      .pipe(catchError(handleError));
  }

  public getLoadedGridData(data: boolean): void {
    this.reloadGrid.next(data);
  }

  public addPlacementRescind(
    payload: PlacementRescindRequest[]
  ): Observable<ApiResponse<PlacementRescindResponse>> {
    return this.http
      .post<ApiResponse<PlacementRescindResponse>>(
        `${this.config.apiUrl}/PlacementFinder/PlacementRescind/Add`,
        payload
      )
      .pipe(catchError(handleError));
  }

  public getServiceSummary(refSk: string): Observable<IepSummaryResponse>  {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/GetServiceSummary/${encodeURIComponent(refSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IepSummaryResponse>(url)
      .pipe(catchError(handleError));
  }
}
